import { Link, useLocation, useSearchParams } from 'react-router';
import {
    Pagination,
    PaginationItem,
    useAppDisplayModeContext,
} from '@archipro-design/aria';
import type { PaginationItemCounterProps } from '@archipro-design/aria';
import type { MagazineDirectoryTileViewModel } from '../../type';
import { MagazineCard } from '../magazine-card/MagazineCard';
import { FilterSection } from '../filter-section/FilterSection';
import type { FilterStates } from '~/modules/magazine/type/filter-states';
import { trimPaginationURL } from '~/modules/pagination/util/trim-pagination-url';
import type { PaginationState } from '@modules/pagination/type';
import type { digitalPublicationFilters } from '../../api/digital-publication-filters.server';

interface PaginatedMagazinesProp {
    magazineTiles: MagazineDirectoryTileViewModel[];
    pagination: PaginationState;
    initialFilterState: FilterStates;
    filters: Awaited<ReturnType<typeof digitalPublicationFilters>>;
}

export const PaginatedMagazines: React.FC<PaginatedMagazinesProp> = ({
    pagination,
    magazineTiles,
    initialFilterState,
    filters,
}) => {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const appDisplayMode = useAppDisplayModeContext();
    const { desktop } = appDisplayMode;

    function createPrefetchLink(page: number) {
        return trimPaginationURL(page, searchParams, location);
    }

    return (
        <div className="space-y-8 md:space-y-12.5">
            {desktop && (
                <FilterSection
                    initialStates={initialFilterState}
                    filters={filters}
                />
            )}
            <div>
                <div className="grid grid-cols-2 gap-x-1.5 gap-y-12 px-1 md:grid-cols-4 md:gap-x-7 md:gap-y-32.5 md:px-0">
                    {magazineTiles.map((magazine) => (
                        <MagazineCard key={magazine.link} magazine={magazine} />
                    ))}
                </div>
                {pagination.totalPages > 1 && (
                    <div className="px-4 pb-9.5 pt-12.5 md:pt-30">
                        <Pagination
                            className="[&_ul]:gap-x-2! [&_ul]:md:gap-x-5!"
                            prevButton={{}}
                            nextButton={{}}
                            renderItem={(item) => {
                                if (
                                    item.type === 'start-ellipsis' ||
                                    item.type === 'end-ellipsis'
                                ) {
                                    return PaginationItem.create({
                                        key: `pagination-item-${item.type}`,
                                        content: item,
                                    });
                                }

                                const disablePrev =
                                    item.type === 'previous' &&
                                    pagination.isFirstPage;
                                const disableNext =
                                    item.type === 'next' &&
                                    pagination.isLastPage;
                                if (disablePrev || disableNext) {
                                    return PaginationItem.create({
                                        key: `pagination-item-${item.page}-${item.type}`,
                                        content: item,
                                    });
                                }

                                return PaginationItem.create({
                                    key: `pagination-item-${item.type}-${item.page}`,
                                    as: Link,
                                    prefetch: 'intent',
                                    to: createPrefetchLink(item.page),
                                    content: item,
                                });
                            }}
                            counter={{
                                range: {
                                    from: pagination.currentRange.from,
                                    to: pagination.currentRange.to,
                                },
                                total: pagination.totalCount,
                                textRenderer: (
                                    range: PaginationItemCounterProps['range'],
                                    total: PaginationItemCounterProps['total'],
                                ) =>
                                    `${range.from} - ${range.to} of ${total} Magazine(s)`,
                            }}
                            divider={null}
                            siblingCount={1}
                            boundaryCount={1}
                            count={pagination.totalPages}
                            totalCount={pagination.totalCount}
                            defaultPage={pagination.page}
                            page={pagination.page}
                            pageSize={pagination.pageSize}
                            appDisplayMode={appDisplayMode}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
